import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoCheckmark, IoReload } from 'react-icons/io5';
import { buildWorkspaceSetupPayload, prepareItems } from './constants';
import { AuthFormShell } from './AuthForm.styles';
import { PrepareItem, PrepareList } from './Onboarding.styles';
import { PendingDot, StatusDot } from './shared/shared.styles';
import onboardingService from '../../../services/onboardingService';
import Toast from '../../molecules/Toast';
import { notifyTourStatusChange } from '../../../hooks/useDummyData';

function OnboardingStep4({ data, onDone }) {
  const [done, setDone] = useState(0);
  const [failed, setFailed] = useState(false);
  const hasSubmitted = useRef(false);
  const navigate = useNavigate();


  const submitOnboarding = async () => {
    if (hasSubmitted.current) return true;
    hasSubmitted.current = true;

    try {
      const res = await onboardingService.saveWorkspaceSetup(
        buildWorkspaceSetupPayload({
          role: data?.role,
          goal: data?.goal,
          business: data?.business,
        }),
      );
      if (!res?.success) {
        throw new Error(res?.message || 'Failed to complete onboarding setup');
      }

      localStorage.removeItem('hasSeenTour');
      notifyTourStatusChange('hasSeenTour');
      return true;
    } catch (error) {
      hasSubmitted.current = false;
      setFailed(true);
      Toast({ type: 'error', message: error?.message || 'Something went wrong' });
      return false;
    }
  };

  useEffect(() => {
    if (failed) return undefined;

    const timer = setInterval(() => {
      setDone(current => {
        if (current >= prepareItems.length) return current;
        return current + 1;
      });
    }, 700);

    return () => clearInterval(timer);
  }, [failed]);

  useEffect(() => {
    if (failed || done === 0) return undefined;

    let cancelled = false;

    const runStepEffects = async () => {
      if (done === 3) {
        const ok = await submitOnboarding();
        if (!ok || cancelled) return;
      }

      if (done >= prepareItems.length) {
        setTimeout(() => {
          if (cancelled) return;
          if (onDone) {
            onDone();
          } else {
            navigate('/ai-onboarding');
          }
        }, 700);
      }
    };

    runStepEffects();

    return () => {
      cancelled = true;
    };
  }, [done, failed, navigate, onDone]);

  const handleRetry = () => {
    setFailed(false);
    setDone(2);
  };

  return (
    <AuthFormShell $centered>
      <div className="auth-heading">
        <h1>
          Preparing your <span className="gradient-text">outbound machine…</span>
        </h1>
        <p>Spinning up your workspace. This takes a few seconds.</p>
      </div>

      <PrepareList>
        {prepareItems.map((label, index) => {
          const isDone = index < done;
          const isCurrent = index === done && !failed;

          return (
            <PrepareItem key={label} $done={isDone} $current={isCurrent}>
              {isDone ? (
                <StatusDot $done>
                  <IoCheckmark strokeWidth={3} />
                </StatusDot>
              ) : isCurrent ? (
                <IoReload size={16} className="spinner" />
              ) : (
                <PendingDot />
              )}
              <span>{label}</span>
            </PrepareItem>
          );
        })}
      </PrepareList>

      {failed && (
        <button type="button" onClick={handleRetry} style={{ marginTop: 20 }}>
          Retry setup
        </button>
      )}
    </AuthFormShell>
  );
}

export default OnboardingStep4;
